import { useEffect, useMemo, useState } from "react";
import { Loader2, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { BTN_PRIMARY, BTN_SECONDARY } from "@/components/checkout/buttonStyles";

/** HK-CONSUMPTION · Manual refill entry: pick room → found qty / refilled qty per item. */
export function RefillDialog({ rooms, items, initialRoomId, onClose, onSaved }) {
  const [roomId, setRoomId] = useState(initialRoomId || "");
  const [lines, setLines] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const room = useMemo(() => rooms.find((r) => r.room_id === roomId), [rooms, roomId]);

  useEffect(() => { setLines({}); }, [roomId]);

  const set = (id, k, v) => setLines((l) => ({ ...l, [id]: { ...(l[id] || {}), [k]: v } }));
  const stockOf = (id) => room?.stock.find((s) => s.item_id === id);
  const fillToPar = () => {
    if (!room) return;
    const next = {};
    room.stock.forEach((s) => { if (s.qty < s.par) next[s.item_id] = { found: String(s.qty), refilled: String(Math.max(s.par - s.qty, 0)) }; });
    setLines(next);
  };
  const payload = Object.entries(lines).map(([item_id, l]) => ({ item_id, refilled_qty: Number(l.refilled || 0), found_qty: l.found === "" || l.found === undefined ? null : Number(l.found) }))
    .filter((l) => l.refilled_qty > 0 || l.found_qty !== null);
  const save = async () => {
    if (!roomId || payload.length === 0) { toast.error("Pick a room and enter at least one refill"); return; }
    setBusy(true);
    try {
      const r = await api.post("/hk/consumption/refill", { room_id: roomId, lines: payload, note: note.trim() || null });
      toast.success(`Refill logged for room ${r.data.room.room_number}`);
      (r.data.store_warnings || []).forEach((w) => toast.warning(`Floor store: ${w}`));
      onSaved?.(r.data); onClose();
    } catch (e) { toast.error(formatApiError(e) || "Could not save refill"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="bg-white border border-[#D5E6E8] md:max-w-[760px] max-h-[92vh] overflow-y-auto p-0" data-hocp-dialog data-testid="refill-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#D5E6E8]">
          <DialogTitle className="font-heading text-[16px] flex items-center gap-2"><PackagePlus size={18} className="text-[#014751]" />Log refill</DialogTitle>
          <DialogDescription className="text-[12.5px] text-[#3F5257]">Enter what you found in the room and how much you topped up. Consumption is derived automatically and the floor store is deducted.</DialogDescription>
        </DialogHeader>
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[200px]">
              <label className="text-[10px] mono uppercase tracking-wider text-[#3F5257]">Room</label>
              <Select value={roomId} onValueChange={setRoomId}>
                <SelectTrigger className="h-10 mt-1 bg-white" data-testid="refill-room-select"><SelectValue placeholder="Select room…" /></SelectTrigger>
                <SelectContent className="max-h-72">{rooms.map((r) => <SelectItem key={r.room_id} value={r.room_id} data-testid={`refill-room-opt-${r.room_number}`}>Room {r.room_number} · F{r.floor} · {r.room_type}{r.low_count ? ` · ${r.low_count} low` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <button type="button" className={`${BTN_SECONDARY} h-10 px-3`} disabled={!room} onClick={fillToPar} data-testid="refill-fill-to-par">Top up everything to par</button>
          </div>
          {room && (
            <table className="w-full text-[12.5px]" data-testid="refill-lines">
              <thead><tr className="text-[10px] mono uppercase tracking-wider text-[#3F5257] border-b border-[#D5E6E8]"><th className="py-1.5 text-left">Item</th><th className="py-1.5 text-right">In room</th><th className="py-1.5 text-right">Par / Min</th><th className="py-1.5 text-right w-28">Found</th><th className="py-1.5 text-right w-28">Refilled</th><th className="py-1.5 text-right">After</th></tr></thead>
              <tbody>
                {items.map((it) => { const s = stockOf(it.item_id); const l = lines[it.item_id] || {}; const found = l.found === "" || l.found === undefined ? (s?.qty ?? 0) : Number(l.found); const after = found + Number(l.refilled || 0); return (
                  <tr key={it.item_id} className={`border-b border-[#EEF4F5] ${s?.low ? "bg-[#FFF4F4]" : ""}`} data-testid={`refill-line-${it.item_id}`}>
                    <td className="py-1.5 font-medium text-[#1A2425]">{it.item_name}<span className="text-[#97A6A9]"> · {it.unit}</span></td>
                    <td className={`py-1.5 text-right mono ${s?.low ? "text-[#B42318] font-semibold" : ""}`}>{s?.qty ?? "—"}</td>
                    <td className="py-1.5 text-right mono text-[#3F5257]">{it.par} / {it.min}</td>
                    <td className="py-1.5 text-right"><Input type="number" min="0" step="1" value={l.found ?? ""} placeholder={String(s?.qty ?? "")} onChange={(e) => set(it.item_id, "found", e.target.value)} className="h-8 w-24 ml-auto text-right text-[12px] mono" data-testid={`refill-found-${it.item_id}`} /></td>
                    <td className="py-1.5 text-right"><Input type="number" min="0" step="1" value={l.refilled ?? ""} placeholder="0" onChange={(e) => set(it.item_id, "refilled", e.target.value)} className="h-8 w-24 ml-auto text-right text-[12px] mono" data-testid={`refill-qty-${it.item_id}`} /></td>
                    <td className={`py-1.5 text-right mono font-semibold ${after < it.min ? "text-[#B42318]" : "text-[#14805E]"}`}>{after}</td>
                  </tr>); })}
              </tbody>
            </table>
          )}
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional) — e.g. departure clean, guest request" className="h-9 text-[12.5px]" data-testid="refill-note" />
          <div className="flex justify-end gap-2">
            <button type="button" className={`${BTN_SECONDARY} h-10 px-4`} onClick={onClose} disabled={busy} data-testid="refill-cancel">Cancel</button>
            <button type="button" className={`${BTN_PRIMARY} h-10 px-4`} onClick={save} disabled={busy || !roomId || payload.length === 0} data-testid="refill-save">{busy ? <Loader2 size={14} className="animate-spin" /> : <PackagePlus size={14} />}Save refill ({payload.length})</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
